import { publish, type ItemTypes } from '@/utils/event-bus'

export const STORAGE_KEY = 'jo_user_saves'

export interface SaveRecord {
  item_id: string
  saved_at: string
  type: ItemTypes
}

export type AddSaveFunction = (userId: string, itemId: string, type: ItemTypes) => Promise<void>
export type SyncSavesFunction = (userId: string) => Promise<void>

export const getUserSaves = (): Map<string, SaveRecord> => {
  const saves = localStorage.getItem(STORAGE_KEY)
  return saves ? new Map(JSON.parse(saves)) : new Map()
}

export const saveItems = (saves: Map<string, SaveRecord>): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(saves.entries())))
}

export const syncSaves: SyncSavesFunction = async (userId) => {
  try {
    const response = await fetch(`/api/saves/${userId}`)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const saves = await response.json()

    const savesObj = new Map<string, SaveRecord>()
    saves.forEach((save: SaveRecord) => {
      savesObj.set(save.item_id, save)
    })

    saveItems(savesObj)
  } catch (err) {
    console.error('Failed to sync saves:', err)
    throw err
  }
}

export const addSave: AddSaveFunction = async (userId, itemId, type) => {
  const saves = getUserSaves()
  if (saves.get(itemId)) return

  const timestamp = new Date().toISOString()
  saves.set(itemId, { item_id: itemId, type, saved_at: timestamp })
  saveItems(saves)
  publish('add_save_item', {
    type,
    item_id: itemId,
  })

  try {
    const response = await fetch(`/api/saves/${userId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, itemId, type, savedAt: timestamp }),
    })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  } catch (err) {
    saves.delete(itemId)
    saveItems(saves)
    throw err
  }
}

export async function removeSave(userId: string, itemId: string, type: ItemTypes): Promise<void> {
  const saves = getUserSaves()
  const removeSave = saves.get(itemId)
  if (!removeSave) return

  saves.delete(itemId)
  saveItems(saves)
  publish('remove_save_item', {
    type,
    item_id: itemId,
  })

  try {
    const response = await fetch(`/api/saves/${userId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemId }),
    })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  } catch (err) {
    saves.set(itemId, removeSave)
    saveItems(saves)
    throw err
  }
}
